<script setup>
import { debounce } from 'lodash-es';
import { BI_DEFAULT_PALETTE_COLORS } from '~/bi/constants/bi-constants';
import { hexToRgb } from '~/common/utils/common.utils';
import 'vue-color/style.css';

const props = defineProps({
  activeColor: {
    type: String,
    default: null,
  },
  type: {
    type: String,
    default: 'plain',
    validator(value) {
      return ['plain', 'outlined'].includes(value);
    },
  },
  appendWhiteToTheStart: {
    type: Boolean,
    default: false,
  },
  appendMixedColorToTheEnd: {
    type: Boolean,
    default: false,
  },
  appendCustomColorPicker: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['colorSelected']);

const MIXED_COLOR = 'linear-gradient(90deg, #FFC278 0%, #F1FF75 30.29%, #68FF93 56.25%, #588AFF 78.37%, #FF6996 100%)';

const ChromePicker = shallowRef(null);

const state = reactive({
  is_menu_open: false,
  custom_color: props.activeColor || '#000000',
  show_custom_picker: false,
});

const colors = computed(() => {
  const color_list = BI_DEFAULT_PALETTE_COLORS.map((color) => {
    return {
      key: color,
      value: color,
    };
  });
  if (props.appendWhiteToTheStart) {
    color_list.unshift({
      key: '#FFFFFF',
      value: '#FFFFFF',
    });
  }
  if (props.appendMixedColorToTheEnd) {
    color_list.push({
      key: 'distinct',
      value: MIXED_COLOR,
    });
  }
  return color_list;
});

function onSelectColor(color) {
  emit('colorSelected', color.key);
}

function onCustomColorChange(color) {
  state.custom_color = color;
  emit('colorSelected', color);
}

async function loadColorPicker() {
  const { ChromePicker: VueColorChromePicker } = await import('vue-color');
  ChromePicker.value = VueColorChromePicker;
}

watch(() => props.activeColor, (new_color) => {
  if (new_color && new_color !== 'distinct')
    state.custom_color = new_color;
});

onMounted(async () => {
  await loadColorPicker();
  if (props.activeColor && props.activeColor !== 'distinct')
    state.custom_color = props.activeColor;
});
</script>

<template>
  <HawkMenu
    position="fixed"
    additional_trigger_classes="p-0 m-0 !ring-0 !border-0 focus:!ring-0"
    @open="state.is_menu_open = true"
    @close="state.is_menu_open = false"
  >
    <template #trigger="{ open }">
      <slot name="trigger">
        <div v-if="props.type === 'plain'" class="w-3 h-3 rounded-full mt-[5px]" :style="props.activeColor === 'distinct' ? { backgroundImage: MIXED_COLOR } : { backgroundColor: props.activeColor }" />
        <div v-else class="border rounded p-1.5 flex items-center gap-1">
          <div
            class="w-3 h-3 rounded-full"
            :class="{ 'border border-gray-200': props.activeColor === '#FFFFFF' }"
            :style="props.activeColor === 'distinct' ? { backgroundImage: MIXED_COLOR } : { backgroundColor: props.activeColor }"
          />
          <IconHawkChevronUp v-if="open" class="w-4 h-4" />
          <IconHawkChevronDown v-else class="w-4 h-4" />
        </div>
      </slot>
    </template>
    <template #content>
      <div class="w-[355px] p-3">
        <div class="flex flex-wrap gap-x-3 gap-y-2">
          <div
            v-for="color in colors"
            :key="color.key"
            class="p-0.5 rounded-full border border-transparent cursor-pointer"
            :class="{
              '!border-gray-300': props.activeColor === color.key,
            }"
            @click="onSelectColor(color)"
          >
            <div
              class="w-4 h-4 rounded-full"
              :class="{ 'border border-gray-200': color.key === '#FFFFFF' }"
              :style="color.key === 'distinct' ? { backgroundImage: color.value } : { backgroundColor: color.value }"
            />
          </div>
        </div>

        <div v-if="props.appendCustomColorPicker" class="mt-3 pt-3 border-t border-gray-200">
          <HawkMenu
            additional_trigger_classes="p-0 m-0 !ring-0 !border-0 focus:!ring-0"
          >
            <template #trigger>
              <div class="text-primary-700 hover:text-primary-800 text-sm font-medium cursor-pointer">
                Pick custom color
              </div>
            </template>
            <template #content>
              <div class="p-3">
                <div class="flex items-center gap-1 mb-3">
                  <div class="text-sm font-semibold text-gray-900">
                    Color picker
                  </div>
                </div>
                <ChromePicker
                  :model-value="state.custom_color"
                  :disable-alpha="true"
                  :disable-fields="false"
                  @update:model-value="onCustomColorChange"
                />
              </div>
            </template>
          </HawkMenu>
        </div>
      </div>
    </template>
  </HawkMenu>
</template>

<style lang="scss">
.vc-chrome-picker {
  box-shadow: none !important;
  .toggle-icon {
    margin-left: 12px;
  }
}
</style>
